import React, { useState, useEffect } from "react";
import {
  X,
  ShieldCheck,
  KeyRound,
  FileSpreadsheet,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  Building2,
  Calendar,
  Layers,
  ArrowRight,
  TrendingUp,
  FileText,
  Lock,
  ChevronLeft,
  Download,
  ExternalLink,
  Clock,
  FileCode,
  Search,
} from "lucide-react";
import moment from "moment";
import axios from "axios";
import {
  generateGstAnalyticsOtpApi,
  submitGstAnalyticsOtpApi,
  getGstAnalyticsStatusApi,
  getPreviousGstReportByGstinApi,
} from "../ActionCreator";

export default function GstAnalysisModal({
  isOpen,
  onClose,
  initialGstin = "",
  initialUsername = "",
  allDetail = {},
  creditData = null,
  loanAppData = null,
}) {
  // Step state: 1 = Generate OTP, 2 = Submit OTP, 3 = Status / Results
  const [currentStep, setCurrentStep] = useState(1);
  const [maxCompletedStep, setMaxCompletedStep] = useState(1);

  // Form states
  const [gstin, setGstin] = useState("");
  const [gstPortalUsername, setGstPortalUsername] = useState("");
  const [reportStartYear, setReportStartYear] = useState("2025");
  const [numberOfYears, setNumberOfYears] = useState("1");
  const [requestId, setRequestId] = useState("");
  const [clientRefNum, setClientRefNum] = useState("");
  const [otp, setOtp] = useState("");

  // Previous report states
  const [prevReportLoading, setPrevReportLoading] = useState(false);
  const [prevReportData, setPrevReportData] = useState(null);
  const [prevReportStatus, setPrevReportStatus] = useState("");

  // UI / Status states
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [statusResponse, setStatusResponse] = useState(null);
  const [jsonReportData, setJsonReportData] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState("overview");

  // Sync initial values when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultGst =
        initialGstin ||
        creditData?.corporateKyc?.gstRegistration ||
        loanAppData?.gst ||
        loanAppData?.gstin ||
        allDetail?.gstin ||
        "";
      if (defaultGst) setGstin(defaultGst.trim().toUpperCase());

      if (initialUsername) {
        setGstPortalUsername(initialUsername);
      }
      setErrorMsg("");
      setSuccessMsg("");
      setPrevReportData(null);
      setPrevReportStatus("");
    }
  }, [isOpen, initialGstin, initialUsername, creditData, loanAppData, allDetail]);

  if (!isOpen) return null;

  const handleCopyRequestId = () => {
    if (requestId) {
      navigator.clipboard.writeText(requestId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Step access controller - enforces mandatory sequence
  const canAccessStep = (targetStep) => {
    if (targetStep === 1) return true;
    if (targetStep === 2) return !!requestId && maxCompletedStep >= 1;
    if (targetStep === 3) return maxCompletedStep >= 2 || !!statusResponse;
    return false;
  };

  const handleStepClick = (targetStep) => {
    if (canAccessStep(targetStep)) {
      setCurrentStep(targetStep);
      setErrorMsg("");
    }
  };

  // Helper to extract clear error/status message
  const extractApiMessage = (payload, defaultMsg) => {
    if (!payload) return defaultMsg;
    const body = payload?.response?.data || payload?.data || payload;
    if (typeof body === "string" && body.trim().length > 0 && body.toLowerCase() !== "failure" && body.toLowerCase() !== "error") {
      return body;
    }
    if (typeof body?.data === "string" && body.data.trim().length > 0 && body.data.toLowerCase() !== "failure") {
      return body.data;
    }
    if (typeof body?.error === "string" && body.error.trim().length > 0) {
      return body.error;
    }
    if (body?.message && body.message.toLowerCase() !== "failure" && body.message.toLowerCase() !== "error" && body.message.toLowerCase() !== "failed") {
      return body.message;
    }
    if (payload?.message && payload.message.toLowerCase() !== "failure" && payload.message.toLowerCase() !== "error") {
      return payload.message;
    }
    return defaultMsg;
  };

  // Check / Fetch previous analysis report by GSTIN
  const handleFetchPreviousReport = () => {
    if (!gstin || gstin.trim().length < 15) {
      setErrorMsg("Please enter a valid 15-digit GSTIN to fetch previous report.");
      return;
    }

    setPrevReportLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    setPrevReportData(null);

    getPreviousGstReportByGstinApi(
      { gstin: gstin.trim().toUpperCase() },
      (res) => {
        setPrevReportLoading(false);
        const data = res?.data || res || {};

        // Extract URLs and metadata from report-links response
        const excelUrl =
          (typeof data?.excel_report_url === "string" && data.excel_report_url) ||
          data?.reports?.excel?.url ||
          data?.result?.reports?.excel?.url ||
          (typeof res?.excel_report_url === "string" && res.excel_report_url) ||
          res?.data?.result?.reports?.excel?.url ||
          null;

        const jsonUrl =
          (typeof data?.json_report_url === "string" && data.json_report_url) ||
          data?.reports?.json?.url ||
          data?.result?.reports?.json?.url ||
          (typeof res?.json_report_url === "string" && res.json_report_url) ||
          res?.data?.result?.reports?.json?.url ||
          null;

        const reqId = data?.request_id || res?.request_id || res?.data?.request_id || "";
        const reportExpiresAt =
          data?.report_expires_at ||
          data?.expires_at ||
          data?.reports?.excel?.expires_at ||
          res?.report_expires_at ||
          null;
        const linksExpiredFlag = data?.links_expired ?? res?.links_expired;
        const stage = data?.stage || res?.stage || "";

        if (excelUrl || jsonUrl) {
          // Check expiration matching against date & time
          const isDateExpired = reportExpiresAt
            ? new Date(reportExpiresAt).getTime() <= Date.now()
            : false;
          const isExpired = linksExpiredFlag === true || isDateExpired;

          const reportInfo = {
            excelUrl,
            jsonUrl,
            excel: { url: excelUrl, expires_at: reportExpiresAt },
            json: { url: jsonUrl, expires_at: reportExpiresAt },
            requestId: reqId,
            isExpired,
            expiresAt: reportExpiresAt,
            stage,
            fullRes: res,
          };

          if (isExpired) {
            setPrevReportStatus("EXPIRED");
            setPrevReportData(reportInfo);
            setErrorMsg(
              `Report expired! Previous analysis file for GSTIN ${gstin.trim().toUpperCase()} expired on ${
                reportExpiresAt ? moment(reportExpiresAt).format("DD MMM YYYY, hh:mm A") : "a prior date"
              }. Please refetch again by generating OTP below.`
            );
          } else {
            setPrevReportStatus("VALID");
            if (reqId) setRequestId(reqId);
            setPrevReportData(reportInfo);
            setSuccessMsg(
              `Valid previous analysis report found! (Valid till: ${
                reportExpiresAt ? moment(reportExpiresAt).format("DD MMM YYYY, hh:mm A") : "Active"
              })`
            );

            // Trigger download if excel link exists
            if (excelUrl) {
              window.open(excelUrl, "_blank");
            }
          }
        } else {
          setPrevReportStatus("NOT_FOUND");
          const fallbackMsg = `No GST analytics report found for the provided GSTIN (${gstin.trim().toUpperCase()}). Please complete the OTP flow first.`;
          setErrorMsg(extractApiMessage(res, fallbackMsg));
        }
      },
      (err) => {
        setPrevReportLoading(false);
        const fallbackMsg = `No GST analytics report found for the provided GSTIN (${gstin.trim().toUpperCase()}). Please complete the OTP flow first.`;
        setErrorMsg(extractApiMessage(err, fallbackMsg));
      }
    );
  };

  // Step 1: Generate OTP
  const handleGenerateOtp = (e) => {
    if (e) e.preventDefault();
    if (!gstin.trim()) {
      setErrorMsg("Please enter a valid GSTIN.");
      return;
    }
    if (!gstPortalUsername.trim()) {
      setErrorMsg("Please enter GST Portal Username.");
      return;
    }
    if (!reportStartYear.trim()) {
      setErrorMsg("Please enter Report Start Year.");
      return;
    }
    if (!numberOfYears || Number(numberOfYears) <= 0) {
      setErrorMsg("Please enter a valid Number of Years (at least 1).");
      return;
    }

    setLoading(true);
    setLoadingText("Sending OTP to registered GST mobile / email...");
    setErrorMsg("");
    setSuccessMsg("");

    generateGstAnalyticsOtpApi(
      {
        gstin: gstin.trim().toUpperCase(),
        gstPortalUsername: gstPortalUsername.trim(),
        reportStartYear: String(reportStartYear).trim(),
        numberOfYears: String(numberOfYears).trim(),
      },
      (res) => {
        setLoading(false);
        const reqId =
          res?.data?.request_id ||
          res?.data?.requestId ||
          res?.request_id ||
          res?.requestId ||
          res?.data?.data?.request_id ||
          res?.data?.data?.requestId ||
          res?.result?.request_id ||
          res?.result?.requestId ||
          res?.reqId ||
          "";

        const refNum = res?.data?.client_ref_num || res?.client_ref_num || "";
        if (refNum) setClientRefNum(refNum);

        const msg =
          res?.data?.message ||
          res?.message ||
          res?.msg ||
          "OTP sent to the contact registered on the GST portal";

        if (reqId) {
          setRequestId(reqId);
          setSuccessMsg(msg);
          setMaxCompletedStep((prev) => Math.max(prev, 2));
          setCurrentStep(2);
        } else if (res?.status === 200 && res?.data && typeof res.data === "string") {
          setRequestId(res.data);
          setMaxCompletedStep((prev) => Math.max(prev, 2));
          setCurrentStep(2);
          setSuccessMsg("OTP request generated successfully.");
        } else {
          setErrorMsg(extractApiMessage(res, "Failed to generate OTP. Please verify the GSTIN and username."));
        }
      },
      (err) => {
        setLoading(false);
        setErrorMsg(extractApiMessage(err, "Failed to connect to GST server."));
      }
    );
  };

  // Step 2: Submit OTP
  const handleSubmitOtp = (e) => {
    if (e) e.preventDefault();
    if (!requestId.trim()) {
      setErrorMsg("Request ID is missing. Please regenerate OTP from Step 1.");
      return;
    }
    if (!otp.trim()) {
      setErrorMsg("Please enter the 6-digit OTP.");
      return;
    }

    setLoading(true);
    setLoadingText("Verifying OTP with GST Portal...");
    setErrorMsg("");
    setSuccessMsg("");

    submitGstAnalyticsOtpApi(
      {
        requestId: requestId.trim(),
        otp: otp.trim(),
      },
      (res) => {
        setLoading(false);
        const msg =
          res?.data?.message ||
          res?.message ||
          "OTP successfully submitted. Report generation started.";

        if (
          res?.status === 200 ||
          res?.data?.http_response_code === 200 ||
          res?.success === true ||
          res?.status === "SUCCESS" ||
          res?.status === "COMPLETED" ||
          res?.status === "IN_PROGRESS" ||
          res?.statusCode === 200 ||
          res?.data
        ) {
          setSuccessMsg(msg);
          setMaxCompletedStep((prev) => Math.max(prev, 3));
          setCurrentStep(3);
          handleCheckStatus(requestId.trim());
        } else if (res?.success === false || res?.error || res?.status === "FAILED") {
          setErrorMsg(extractApiMessage(res, "Invalid OTP or OTP verification failed."));
        } else {
          setSuccessMsg("OTP submitted. Fetching analytics report...");
          setMaxCompletedStep((prev) => Math.max(prev, 3));
          setCurrentStep(3);
          handleCheckStatus(requestId.trim());
        }
      },
      (err) => {
        setLoading(false);
        setErrorMsg(extractApiMessage(err, "Failed to submit OTP."));
      }
    );
  };

  // Step 3: Check Status & Fetch Reports
  const handleCheckStatus = (targetReqId) => {
    const rId = targetReqId || requestId;
    if (!rId.trim()) {
      setErrorMsg("Please provide a Request ID to check status.");
      return;
    }

    setLoading(true);
    setLoadingText("Fetching GST Analytics status & download links...");
    setErrorMsg("");

    getGstAnalyticsStatusApi(
      { requestId: rId.trim() },
      (res) => {
        setLoading(false);
        if (res) {
          setStatusResponse(res);
          const reports =
            res?.data?.result?.reports ||
            res?.result?.reports ||
            res?.data?.reports ||
            {};

          const refNum = res?.data?.client_ref_num || res?.client_ref_num || "";
          if (refNum) setClientRefNum(refNum);

          if (reports?.excel?.url || reports?.json?.url) {
            setSuccessMsg("GST Analytics Report generated successfully! Excel & JSON downloads are ready.");

            // Attempt to fetch json report content for in-modal preview
            if (reports?.json?.url) {
              fetchJsonReportContent(reports.json.url);
            }
          } else {
            const st = res?.status || res?.data?.status || res?.result?.status || "";
            if (st === "COMPLETED" || st === "SUCCESS") {
              setSuccessMsg("Status retrieved.");
            } else {
              setSuccessMsg("Report generation is in progress. Please click 'Refresh Status' in a few seconds.");
            }
          }
        } else {
          setErrorMsg("No response received from status endpoint.");
        }
      },
      (err) => {
        setLoading(false);
        setErrorMsg(extractApiMessage(err, "Error fetching status."));
      }
    );
  };

  // Fetch JSON report data from S3 URL for dashboard visualization
  const fetchJsonReportContent = (url) => {
    if (!url) return;
    axios
      .get(url)
      .then((response) => {
        if (response?.data) {
          setJsonReportData(response.data);
        }
      })
      .catch((e) => {
        console.warn("Could not pre-fetch JSON report data for inline view:", e);
      });
  };

  // Extract report download URLs & meta
  const reports =
    statusResponse?.data?.result?.reports ||
    statusResponse?.result?.reports ||
    statusResponse?.data?.reports ||
    null;

  const excelReport = reports?.excel || null;
  const jsonReport = reports?.json || null;
  const reportVersion =
    statusResponse?.data?.result?.gst_analytics_report_version ||
    statusResponse?.result?.gst_analytics_report_version ||
    "v1";

  // Helper to extract nested result from status or fetched json report
  const parsedData =
    jsonReportData?.result ||
    jsonReportData?.data ||
    jsonReportData ||
    statusResponse?.data?.result ||
    statusResponse?.result ||
    statusResponse?.data ||
    {};

  const taxpayer =
    parsedData?.taxpayerDetails ||
    parsedData?.taxpayer_details ||
    parsedData?.taxPayerDetails ||
    {};

  const statusState =
    excelReport || jsonReport
      ? "REPORT READY"
      : statusResponse?.status === 200 || statusResponse?.data?.http_response_code === 200
      ? "COMPLETED"
      : statusResponse?.status ||
        statusResponse?.data?.status ||
        (statusResponse ? "RECEIVED" : "");

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.72)",
        backdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: 18,
          width: "95vw",
          maxWidth: "1400px",
          height: "92vh",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.35)",
          border: "1px solid #cbd5e1",
          overflow: "hidden",
        }}
      >
        {/* ── Header ────────────────────────────────────────────── */}
        <div
          style={{
            padding: "18px 28px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(135deg, #024e5a 0%, #036b7b 100%)",
            color: "#ffffff",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                backgroundColor: "rgba(255, 255, 255, 0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FileSpreadsheet style={{ width: 22, height: 22, color: "#ffffff" }} />
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "#ffffff", letterSpacing: "0.01em" }}>
                Digitap GST Analytics & Verification
              </h4>
              <p style={{ margin: 0, fontSize: "12.5px", opacity: 0.9 }}>
                Generate OTP &rarr; Verify OTP &rarr; Download Analytical Excel & Report
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.18)",
              border: "none",
              borderRadius: "10px",
              padding: "8px",
              cursor: "pointer",
              color: "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.2s",
            }}
          >
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* ── Strict Sequential Stepper Bar ──────────────────────── */}
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc",
            padding: "10px 28px",
            gap: 16,
            alignItems: "center",
          }}
        >
          {[
            { step: 1, label: "1. Input Details & Generate OTP", icon: KeyRound },
            { step: 2, label: "2. Verify OTP", icon: ShieldCheck },
            { step: 3, label: "3. GST Analytics & Excel Download", icon: TrendingUp },
          ].map((item, idx) => {
            const Icon = item.icon;
            const isActive = currentStep === item.step;
            const isAccessible = canAccessStep(item.step);
            const isCompleted = maxCompletedStep > item.step;

            return (
              <React.Fragment key={item.step}>
                <button
                  type="button"
                  onClick={() => handleStepClick(item.step)}
                  disabled={!isAccessible}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "9px 16px",
                    borderRadius: 10,
                    border: "none",
                    background: isActive
                      ? "#024e5a"
                      : isCompleted
                      ? "#e0f2fe"
                      : isAccessible
                      ? "#ffffff"
                      : "#f1f5f9",
                    color: isActive
                      ? "#ffffff"
                      : isCompleted
                      ? "#0369a1"
                      : isAccessible
                      ? "#334155"
                      : "#94a3b8",
                    fontWeight: isActive ? 700 : 600,
                    fontSize: "13px",
                    cursor: isAccessible ? "pointer" : "not-allowed",
                    opacity: isAccessible ? 1 : 0.6,
                    boxShadow: isActive ? "0 2px 4px rgba(2,78,90,0.2)" : "none",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                  }}
                  title={!isAccessible ? "Complete previous steps first" : ""}
                >
                  <Icon style={{ width: 15, height: 15 }} />
                  <span>{item.label}</span>
                  {isCompleted && <CheckCircle2 style={{ width: 14, height: 14, color: "#0284c7" }} />}
                  {!isAccessible && <Lock style={{ width: 12, height: 12, color: "#94a3b8", marginLeft: 4 }} />}
                </button>
                {idx < 2 && (
                  <span style={{ color: "#cbd5e1", fontWeight: 700, fontSize: "14px" }}>&rarr;</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Body ──────────────────────────────────────────────── */}
        <div
          style={{
            padding: "24px 28px",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          {/* Alert messages */}
          {errorMsg && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#b91c1c",
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: "13.5px",
              }}
            >
              <AlertCircle style={{ width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#15803d",
                borderRadius: 10,
                padding: "12px 16px",
                fontSize: "13.5px",
              }}
            >
              <CheckCircle2 style={{ width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{successMsg}</span>
            </div>
          )}

          {/* ── STEP 1: Generate OTP & Fetch Prev Report ─────────── */}
          {currentStep === 1 && (
            <form onSubmit={handleGenerateOtp} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "22px",
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 20,
                }}
              >
                {/* GSTIN Input with Fetch Prev Analysis Button */}
                <div style={{ gridColumn: "span 2" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ ...styles.label, margin: 0 }}>
                      <Building2 style={{ width: 14, height: 14, display: "inline", marginRight: 6 }} />
                      GSTIN (15 Digits) *
                    </label>

                    {gstin?.trim().length === 15 && (
                      <button
                        type="button"
                        onClick={handleFetchPreviousReport}
                        disabled={prevReportLoading}
                        style={{
                          background: "#ecfdf5",
                          border: "1px solid #a7f3d0",
                          color: "#047857",
                          fontSize: "12px",
                          fontWeight: 700,
                          padding: "5px 12px",
                          borderRadius: "7px",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          transition: "all 0.15s ease",
                        }}
                      >
                        {prevReportLoading ? (
                          <>
                            <RefreshCw style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} />
                            <span>Checking Previous File...</span>
                          </>
                        ) : (
                          <>
                            <FileSpreadsheet style={{ width: 13, height: 13 }} />
                            <span>Fetch Previous Analysis File</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <input
                    type="text"
                    placeholder="e.g. 07AAACR4249P1ZX"
                    maxLength={15}
                    value={gstin}
                    onChange={(e) => {
                      setGstin(e.target.value.toUpperCase());
                      setPrevReportData(null);
                      setPrevReportStatus("");
                    }}
                    style={{ ...styles.input, textTransform: "uppercase", fontWeight: "600" }}
                    required
                  />
                  <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: 4, display: "block" }}>
                    GST Identification Number of the applicant business
                  </span>
                </div>

                {/* Previous Report Active Notification (Valid or Expired) */}
                {prevReportData && (
                  <div style={{ gridColumn: "span 2" }}>
                    {prevReportData.isExpired ? (
                      <div
                        style={{
                          background: "#fffbeb",
                          border: "1.5px solid #fde68a",
                          borderRadius: 12,
                          padding: "14px 18px",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          color: "#92400e",
                        }}
                      >
                        <AlertCircle style={{ width: 20, height: 20, color: "#d97706", flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "13.5px" }}>
                            Report Expired &mdash; Please Refetch Again
                          </div>
                          <div style={{ fontSize: "12.5px", marginTop: 3, lineHeight: 1.4 }}>
                            The previous GST analytics report for this GSTIN expired on{" "}
                            <span style={{ fontWeight: 700 }}>
                              {prevReportData.expiresAt
                                ? moment(prevReportData.expiresAt).format("DD MMM YYYY, hh:mm A")
                                : "a prior date"}
                            </span>
                            . Please enter your portal credentials below and click <strong>"Generate & Send OTP"</strong> to fetch fresh report links.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                          border: "1.5px solid #86efac",
                          borderRadius: 12,
                          padding: "16px 20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 14,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 10,
                              backgroundColor: "#16a34a",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#ffffff",
                            }}
                          >
                            <FileSpreadsheet style={{ width: 22, height: 22 }} />
                          </div>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontWeight: 700, fontSize: "14px", color: "#14532d" }}>
                                Valid Previous Analysis Report Found
                              </span>
                              <span
                                style={{
                                  background: "#16a34a",
                                  color: "#ffffff",
                                  fontSize: "10.5px",
                                  fontWeight: 700,
                                  padding: "2px 8px",
                                  borderRadius: 10,
                                }}
                              >
                                Active
                              </span>
                            </div>
                            <div style={{ fontSize: "12px", color: "#166534", marginTop: 2 }}>
                              Expires on:{" "}
                              <strong>
                                {prevReportData.expiresAt
                                  ? moment(prevReportData.expiresAt).format("DD MMM YYYY, hh:mm A")
                                  : "Active"}
                              </strong>
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                          {(prevReportData.excelUrl || prevReportData.excel?.url) && (
                            <a
                              href={prevReportData.excelUrl || prevReportData.excel?.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              style={{
                                ...styles.primaryBtn,
                                background: "#16a34a",
                                color: "#ffffff",
                                padding: "8px 16px",
                                fontSize: "12.5px",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Download style={{ width: 14, height: 14 }} />
                              <span>Download Excel (.xlsx)</span>
                            </a>
                          )}

                          <button
                            type="button"
                            onClick={() => {
                              const syntheticStatus = prevReportData.fullRes?.data?.result
                                ? prevReportData.fullRes
                                : {
                                    status: 200,
                                    data: {
                                      http_response_code: 200,
                                      client_ref_num: prevReportData.fullRes?.client_ref_num || "",
                                      request_id: prevReportData.requestId,
                                      result: {
                                        gst_analytics_report_version: "v1",
                                        reports: {
                                          excel: {
                                            url: prevReportData.excelUrl || prevReportData.excel?.url,
                                            expires_at: prevReportData.expiresAt,
                                          },
                                          json: {
                                            url: prevReportData.jsonUrl || prevReportData.json?.url,
                                            expires_at: prevReportData.expiresAt,
                                          },
                                        },
                                      },
                                    },
                                  };
                              setStatusResponse(syntheticStatus);
                              const targetJsonUrl = prevReportData.jsonUrl || prevReportData.json?.url;
                              if (targetJsonUrl) {
                                fetchJsonReportContent(targetJsonUrl);
                              }
                              if (prevReportData.requestId) {
                                setRequestId(prevReportData.requestId);
                              }
                              setMaxCompletedStep(3);
                              setCurrentStep(3);
                            }}
                            style={{
                              ...styles.outlineBtn,
                              borderColor: "#86efac",
                              color: "#15803d",
                              background: "#ffffff",
                              padding: "8px 14px",
                              fontSize: "12.5px",
                            }}
                          >
                            <span>View Full Analysis</span>
                            <ArrowRight style={{ width: 13, height: 13 }} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* GST Portal Username */}
                <div>
                  <label style={styles.label}>
                    <KeyRound style={{ width: 14, height: 14, display: "inline", marginRight: 6 }} />
                    GST Portal Username *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Zerodue_mohit"
                    value={gstPortalUsername}
                    onChange={(e) => setGstPortalUsername(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: 4, display: "block" }}>
                    Registered username on the GST portal
                  </span>
                </div>

                {/* Number of Years (Input) */}
                <div>
                  <label style={styles.label}>
                    <Layers style={{ width: 14, height: 14, display: "inline", marginRight: 6 }} />
                    Number of Years *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    placeholder="e.g. 1"
                    value={numberOfYears}
                    onChange={(e) => setNumberOfYears(e.target.value)}
                    style={styles.input}
                    required
                  />
                  <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: 4, display: "block" }}>
                    Number of financial years to include in the analytics
                  </span>
                </div>

                {/* Custom Report Start Year Input */}
                <div style={{ gridColumn: "span 2" }}>
                  <label style={styles.label}>
                    <Calendar style={{ width: 14, height: 14, display: "inline", marginRight: 6 }} />
                    Report Start Year (Custom Year) *
                  </label>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: 10,
                        background: "#ffffff",
                        overflow: "hidden",
                        maxWidth: "240px",
                        flex: 1,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setReportStartYear((prev) => String(Math.max(2000, Number(prev || 2025) - 1)))
                        }
                        style={{
                          border: "none",
                          background: "#f1f5f9",
                          padding: "10px 16px",
                          cursor: "pointer",
                          fontWeight: "bold",
                          color: "#334155",
                          fontSize: "16px",
                        }}
                      >
                        &minus;
                      </button>
                      <input
                        type="number"
                        min="2000"
                        max="2099"
                        placeholder="YYYY (e.g. 2025)"
                        value={reportStartYear}
                        onChange={(e) => setReportStartYear(e.target.value)}
                        style={{
                          ...styles.input,
                          border: "none",
                          textAlign: "center",
                          fontWeight: "700",
                          fontSize: "15px",
                          padding: "10px",
                        }}
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setReportStartYear((prev) => String(Math.min(2099, Number(prev || 2025) + 1)))
                        }
                        style={{
                          border: "none",
                          background: "#f1f5f9",
                          padding: "10px 16px",
                          cursor: "pointer",
                          fontWeight: "bold",
                          color: "#334155",
                          fontSize: "16px",
                        }}
                      >
                        +
                      </button>
                    </div>

                    {/* Quick year pills */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {["2026", "2025", "2024", "2023", "2022"].map((yr) => (
                        <button
                          key={yr}
                          type="button"
                          onClick={() => setReportStartYear(yr)}
                          style={{
                            fontSize: "12px",
                            padding: "6px 14px",
                            borderRadius: "8px",
                            border: reportStartYear === yr ? "1.5px solid #024e5a" : "1px solid #cbd5e1",
                            background: reportStartYear === yr ? "#e6f4f6" : "#ffffff",
                            color: reportStartYear === yr ? "#024e5a" : "#475569",
                            fontWeight: reportStartYear === yr ? "700" : "500",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {yr}
                        </button>
                      ))}
                    </div>
                  </div>
                  <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: 4, display: "block" }}>
                    Select or type the starting financial calendar year for the report
                  </span>
                </div>
              </div>

              {/* Step 1 Action Button */}
              <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <button
                  type="submit"
                  disabled={loading || !gstin || !gstPortalUsername || !reportStartYear || !numberOfYears}
                  style={{
                    ...styles.primaryBtn,
                    padding: "12px 28px",
                    fontSize: "14px",
                    opacity:
                      loading || !gstin || !gstPortalUsername || !reportStartYear || !numberOfYears
                        ? 0.6
                        : 1,
                    cursor:
                      loading || !gstin || !gstPortalUsername || !reportStartYear || !numberOfYears
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {loading ? (
                    <>
                      <RefreshCw style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} />
                      {loadingText || "Sending OTP..."}
                    </>
                  ) : (
                    <>
                      <span>Generate & Send OTP</span>
                      <ArrowRight style={{ width: 16, height: 16 }} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 2: Submit OTP ─────────────────────────────── */}
          {currentStep === 2 && (
            <form onSubmit={handleSubmitOtp} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "22px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                }}
              >
                {/* Active Request ID Badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#ecfdf5",
                    border: "1px solid #a7f3d0",
                    padding: "14px 18px",
                    borderRadius: 10,
                  }}
                >
                  <div>
                    <div style={{ fontSize: "11.5px", color: "#065f46", fontWeight: 700, letterSpacing: "0.04em" }}>
                      ACTIVE REQUEST ID
                    </div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#047857", fontFamily: "monospace", marginTop: 2 }}>
                      {requestId || "Not generated yet"}
                    </div>
                    {clientRefNum && (
                      <div style={{ fontSize: "11.5px", color: "#047857", marginTop: 4 }}>
                        Client Ref Num: <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{clientRefNum}</span>
                      </div>
                    )}
                  </div>
                  {requestId && (
                    <button
                      type="button"
                      onClick={handleCopyRequestId}
                      style={{
                        background: "#ffffff",
                        border: "1px solid #a7f3d0",
                        padding: "6px 12px",
                        borderRadius: 8,
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#047857",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {copied ? <Check style={{ width: 13, height: 13 }} /> : <Copy style={{ width: 13, height: 13 }} />}
                      {copied ? "Copied" : "Copy ID"}
                    </button>
                  )}
                </div>

                {/* OTP Input */}
                <div style={{ maxWidth: "420px", margin: "10px auto 0", width: "100%" }}>
                  <label style={{ ...styles.label, textAlign: "center", fontSize: "13px" }}>
                    Enter 6-Digit OTP Received by Signatory *
                  </label>
                  <input
                    type="text"
                    maxLength={8}
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    style={{
                      ...styles.input,
                      fontSize: "22px",
                      letterSpacing: "6px",
                      fontWeight: 700,
                      textAlign: "center",
                      padding: "12px",
                    }}
                    autoFocus
                    required
                  />
                  <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: 6, display: "block", textAlign: "center" }}>
                    Please enter the OTP sent to the authorized contact number registered with GSTN.
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  style={{ ...styles.outlineBtn, display: "flex", alignItems: "center", gap: 6 }}
                >
                  <ChevronLeft style={{ width: 14, height: 14 }} />
                  Back to Step 1
                </button>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    type="button"
                    onClick={handleGenerateOtp}
                    disabled={loading}
                    style={styles.outlineBtn}
                  >
                    Resend OTP
                  </button>

                  <button
                    type="submit"
                    disabled={loading || !otp || !requestId}
                    style={{
                      ...styles.primaryBtn,
                      padding: "12px 28px",
                      fontSize: "14px",
                      opacity: loading || !otp || !requestId ? 0.6 : 1,
                      cursor: loading || !otp || !requestId ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? (
                      <>
                        <RefreshCw style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} />
                        {loadingText || "Verifying OTP..."}
                      </>
                    ) : (
                      <>
                        <span>Submit & Verify OTP</span>
                        <CheckCircle2 style={{ width: 16, height: 16 }} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* ── STEP 3: Status & Report with Excel Download ────────────────────────── */}
          {currentStep === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18, height: "100%" }}>
              {/* Request ID bar & Refresh button */}
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  padding: "14px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <div>
                    <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 700, display: "block" }}>
                      REQUEST ID
                    </span>
                    <span style={{ fontSize: "13.5px", fontWeight: 700, color: "#024e5a", fontFamily: "monospace" }}>
                      {requestId || "N/A"}
                    </span>
                  </div>

                  {clientRefNum && (
                    <div>
                      <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 700, display: "block" }}>
                        CLIENT REF
                      </span>
                      <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#334155", fontFamily: "monospace" }}>
                        {clientRefNum}
                      </span>
                    </div>
                  )}

                  {reportVersion && (
                    <div>
                      <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 700, display: "block" }}>
                        REPORT VERSION
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>
                        {reportVersion.toUpperCase()}
                      </span>
                    </div>
                  )}

                  {requestId && (
                    <button
                      type="button"
                      onClick={handleCopyRequestId}
                      style={{
                        background: "#e2e8f0",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: 6,
                        fontSize: "11.5px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        alignSelf: "center",
                      }}
                    >
                      {copied ? <Check style={{ width: 12, height: 12 }} /> : <Copy style={{ width: 12, height: 12 }} />}
                      {copied ? "Copied" : "Copy ID"}
                    </button>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span
                    style={{
                      padding: "6px 14px",
                      borderRadius: 20,
                      fontSize: "12.5px",
                      fontWeight: 700,
                      backgroundColor:
                        statusState === "REPORT READY" || statusState === "COMPLETED" || statusState === "SUCCESS"
                          ? "#dcfce7"
                          : statusState === "IN_PROGRESS" || statusState === "INITIATED"
                          ? "#fef9c3"
                          : "#fee2e2",
                      color:
                        statusState === "REPORT READY" || statusState === "COMPLETED" || statusState === "SUCCESS"
                          ? "#15803d"
                          : statusState === "IN_PROGRESS" || statusState === "INITIATED"
                          ? "#854d0e"
                          : "#b91c1c",
                    }}
                  >
                    {statusState}
                  </span>

                  <button
                    onClick={() => handleCheckStatus(requestId)}
                    disabled={loading || !requestId}
                    style={{
                      ...styles.primaryBtn,
                      padding: "8px 18px",
                      opacity: loading || !requestId ? 0.6 : 1,
                    }}
                  >
                    <RefreshCw
                      style={{
                        width: 14,
                        height: 14,
                        animation: loading ? "spin 1s linear infinite" : "none",
                      }}
                    />
                    <span>{loading ? "Refreshing..." : "Refresh Status"}</span>
                  </button>
                </div>
              </div>

              {/* ── HIGHLIGHTED DOWNLOAD CARDS (EXCEL & JSON) ── */}
              {reports && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
                  {/* Excel Report Card */}
                  {excelReport?.url && (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                        border: "1.5px solid #86efac",
                        borderRadius: 14,
                        padding: "20px 24px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 14,
                        boxShadow: "0 4px 12px rgba(22, 163, 74, 0.08)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                        <div
                          style={{
                            width: 46,
                            height: 46,
                            borderRadius: 12,
                            backgroundColor: "#16a34a",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            flexShrink: 0,
                          }}
                        >
                          <FileSpreadsheet style={{ width: 26, height: 26 }} />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#14532d" }}>
                              GST Analytics Excel Report (.xlsx)
                            </h5>
                            <span
                              style={{
                                background: "#16a34a",
                                color: "#ffffff",
                                fontSize: "10.5px",
                                fontWeight: 700,
                                padding: "2px 8px",
                                borderRadius: 10,
                              }}
                            >
                              Ready
                            </span>
                          </div>
                          <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "#166534", lineHeight: 1.4 }}>
                            Contains structured GSTR-1, GSTR-3B monthly turnover breakdown, reconciliation, and financial analysis model.
                          </p>
                          {excelReport?.expires_at && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: "11.5px", color: "#15803d" }}>
                              <Clock style={{ width: 12, height: 12 }} />
                              <span>Expires: {moment(excelReport.expires_at).format("DD MMM YYYY, hh:mm A")}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <a
                          href={excelReport.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          style={{
                            ...styles.primaryBtn,
                            background: "#16a34a",
                            color: "#ffffff",
                            padding: "10px 20px",
                            textDecoration: "none",
                            fontSize: "13.5px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow: "0 2px 6px rgba(22, 163, 74, 0.25)",
                          }}
                        >
                          <Download style={{ width: 16, height: 16 }} />
                          <span>Download Excel (.xlsx)</span>
                        </a>

                        <a
                          href={excelReport.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            ...styles.outlineBtn,
                            borderColor: "#86efac",
                            color: "#15803d",
                            background: "#ffffff",
                            padding: "10px 16px",
                            textDecoration: "none",
                            fontSize: "13px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <ExternalLink style={{ width: 14, height: 14 }} />
                          <span>Open Link</span>
                        </a>
                      </div>
                    </div>
                  )}

                  {/* JSON Report Card */}
                  {jsonReport?.url && (
                    <div
                      style={{
                        background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                        border: "1.5px solid #7dd3fc",
                        borderRadius: 14,
                        padding: "20px 24px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: 14,
                        boxShadow: "0 4px 12px rgba(2, 132, 199, 0.08)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                        <div
                          style={{
                            width: 46,
                            height: 46,
                            borderRadius: 12,
                            backgroundColor: "#0284c7",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ffffff",
                            flexShrink: 0,
                          }}
                        >
                          <FileCode style={{ width: 26, height: 26 }} />
                        </div>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#0c4a6e" }}>
                              GST Analytics JSON Report (.json)
                            </h5>
                            <span
                              style={{
                                background: "#0284c7",
                                color: "#ffffff",
                                fontSize: "10.5px",
                                fontWeight: 700,
                                padding: "2px 8px",
                                borderRadius: 10,
                              }}
                            >
                              Ready
                            </span>
                          </div>
                          <p style={{ margin: "4px 0 0", fontSize: "12.5px", color: "#0369a1", lineHeight: 1.4 }}>
                            Raw digitized analytics dataset containing detailed filing tables, ratios, and taxpayer metadata.
                          </p>
                          {jsonReport?.expires_at && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: "11.5px", color: "#0284c7" }}>
                              <Clock style={{ width: 12, height: 12 }} />
                              <span>Expires: {moment(jsonReport.expires_at).format("DD MMM YYYY, hh:mm A")}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <a
                          href={jsonReport.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          style={{
                            ...styles.primaryBtn,
                            background: "#0284c7",
                            color: "#ffffff",
                            padding: "10px 20px",
                            textDecoration: "none",
                            fontSize: "13.5px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            boxShadow: "0 2px 6px rgba(2, 132, 199, 0.25)",
                          }}
                        >
                          <Download style={{ width: 16, height: 16 }} />
                          <span>Download JSON</span>
                        </a>

                        <a
                          href={jsonReport.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            ...styles.outlineBtn,
                            borderColor: "#7dd3fc",
                            color: "#0369a1",
                            background: "#ffffff",
                            padding: "10px 16px",
                            textDecoration: "none",
                            fontSize: "13px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <ExternalLink style={{ width: 14, height: 14 }} />
                          <span>View JSON</span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Status Report Contents Tabs */}
              {statusResponse ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, flex: 1, marginTop: 6 }}>
                  {/* Sub-tabs for Report */}
                  <div
                    style={{
                      display: "flex",
                      borderBottom: "1.5px solid #e2e8f0",
                      gap: 12,
                    }}
                  >
                    {[
                      { id: "overview", label: "Overview & Profile" },
                      { id: "filings", label: "Filings & Compliance" },
                      { id: "turnover", label: "Turnover & Monthly Summary" },
                      { id: "raw", label: "Raw Response & JSON Viewer" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveReportTab(tab.id)}
                        style={{
                          background: "none",
                          border: "none",
                          borderBottom: activeReportTab === tab.id ? "2.5px solid #024e5a" : "2.5px solid transparent",
                          color: activeReportTab === tab.id ? "#024e5a" : "#64748b",
                          fontWeight: activeReportTab === tab.id ? 700 : 600,
                          fontSize: "13.5px",
                          padding: "10px 18px",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* TAB 1: Overview */}
                  {activeReportTab === "overview" && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
                      <ReportCard
                        label="Legal Name"
                        value={taxpayer?.legalName || taxpayer?.lgnm || parsedData?.legalName || parsedData?.businessName || "Available in Report"}
                      />
                      <ReportCard
                        label="Trade Name"
                        value={taxpayer?.tradeName || taxpayer?.trade_name || parsedData?.tradeName || "Available in Report"}
                      />
                      <ReportCard
                        label="GSTIN"
                        value={parsedData?.gstin || taxpayer?.gstin || gstin || "N/A"}
                      />
                      <ReportCard
                        label="Status"
                        value={taxpayer?.status || taxpayer?.sts || parsedData?.gstinStatus || statusState || "Active"}
                        highlight
                      />
                      <ReportCard
                        label="Constitution"
                        value={taxpayer?.constitutionOfBusiness || taxpayer?.ctb || parsedData?.constitution || "Available in Report"}
                      />
                      <ReportCard
                        label="Registration Date"
                        value={taxpayer?.dateOfRegistration || taxpayer?.rgdt || parsedData?.registrationDate || "Available in Report"}
                      />
                      <ReportCard
                        label="Report Coverage"
                        value={`${numberOfYears} Year(s) starting ${reportStartYear}`}
                      />
                      <ReportCard
                        label="Excel Download"
                        value={excelReport?.url ? "Generated (.xlsx)" : "Pending"}
                        highlight={!!excelReport?.url}
                      />
                    </div>
                  )}

                  {/* TAB 2: Filings & Compliance */}
                  {activeReportTab === "filings" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {Array.isArray(parsedData?.filingHistory || parsedData?.filings || parsedData?.returns) &&
                      (parsedData?.filingHistory || parsedData?.filings || parsedData?.returns).length > 0 ? (
                        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                          <table style={styles.table}>
                            <thead>
                              <tr style={{ background: "#f8fafc" }}>
                                <th style={styles.th}>Financial Year</th>
                                <th style={styles.th}>Tax Period</th>
                                <th style={styles.th}>Return Type</th>
                                <th style={styles.th}>Date of Filing</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>ARN</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(parsedData?.filingHistory || parsedData?.filings || parsedData?.returns).map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                  <td style={styles.td}>{row?.financialYear || row?.fy || "-"}</td>
                                  <td style={styles.td}>{row?.taxPeriod || row?.period || "-"}</td>
                                  <td style={styles.td}>
                                    <span style={{ fontWeight: 600, color: "#0369a1" }}>
                                      {row?.returnType || row?.return_type || "GSTR-3B"}
                                    </span>
                                  </td>
                                  <td style={styles.td}>{row?.dateOfFiling || row?.dof || "-"}</td>
                                  <td style={styles.td}>
                                    <span
                                      style={{
                                        padding: "3px 10px",
                                        borderRadius: 12,
                                        fontSize: "11.5px",
                                        fontWeight: 600,
                                        background: (row?.status || "").toUpperCase() === "FILED" ? "#dcfce7" : "#fee2e2",
                                        color: (row?.status || "").toUpperCase() === "FILED" ? "#15803d" : "#b91c1c",
                                      }}
                                    >
                                      {row?.status || "Filed"}
                                    </span>
                                  </td>
                                  <td style={styles.td}>{row?.arn || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ padding: "40px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 10 }}>
                          <FileSpreadsheet style={{ width: 38, height: 38, margin: "0 auto 10px", color: "#16a34a" }} />
                          <p style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                            Detailed Filing Compliance is available in the Excel Report
                          </p>
                          <p style={{ margin: "6px 0 16px", fontSize: "12.5px" }}>
                            Download the generated Excel file above for the full month-by-month GSTR filing status.
                          </p>
                          {excelReport?.url && (
                            <a
                              href={excelReport.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              style={{
                                ...styles.primaryBtn,
                                background: "#16a34a",
                                color: "#ffffff",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Download style={{ width: 14, height: 14 }} />
                              <span>Download Excel Report</span>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB 3: Turnover Summary */}
                  {activeReportTab === "turnover" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {Array.isArray(parsedData?.monthlyTurnover || parsedData?.turnover || parsedData?.sales) &&
                      (parsedData?.monthlyTurnover || parsedData?.turnover || parsedData?.sales).length > 0 ? (
                        <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                          <table style={styles.table}>
                            <thead>
                              <tr style={{ background: "#f8fafc" }}>
                                <th style={styles.th}>Month / Period</th>
                                <th style={styles.th}>GSTR-1 Turnover</th>
                                <th style={styles.th}>GSTR-3B Turnover</th>
                                <th style={styles.th}>Tax Paid (INR)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(parsedData?.monthlyTurnover || parsedData?.turnover || parsedData?.sales).map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                  <td style={styles.td}>{item?.month || item?.period || "-"}</td>
                                  <td style={styles.td}>{item?.gstr1Turnover || item?.gstr1 || "-"}</td>
                                  <td style={styles.td}>{item?.gstr3bTurnover || item?.gstr3b || "-"}</td>
                                  <td style={styles.td}>{item?.taxPaid || item?.tax || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ padding: "40px", textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 10 }}>
                          <TrendingUp style={{ width: 38, height: 38, margin: "0 auto 10px", color: "#024e5a" }} />
                          <p style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                            Turnover Analytics Available in Excel Report
                          </p>
                          <p style={{ margin: "6px 0 16px", fontSize: "12.5px" }}>
                            The complete monthly breakdown of GSTR-3B vs GSTR-1 turnover is formatted in the downloadable Excel workbook.
                          </p>
                          {excelReport?.url && (
                            <a
                              href={excelReport.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              style={{
                                ...styles.primaryBtn,
                                background: "#024e5a",
                                color: "#ffffff",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Download style={{ width: 14, height: 14 }} />
                              <span>Download Excel (.xlsx)</span>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* TAB 4: Raw JSON Response */}
                  {activeReportTab === "raw" && (
                    <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column" }}>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(
                            JSON.stringify(jsonReportData || statusResponse, null, 2)
                          );
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        style={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          background: "#334155",
                          color: "#ffffff",
                          border: "none",
                          padding: "6px 14px",
                          borderRadius: 8,
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          zIndex: 10,
                        }}
                      >
                        {copied ? <Check style={{ width: 13, height: 13 }} /> : <Copy style={{ width: 13, height: 13 }} />}
                        {copied ? "Copied!" : "Copy Full JSON"}
                      </button>
                      <pre
                        style={{
                          background: "#0f172a",
                          color: "#38bdf8",
                          padding: "20px",
                          borderRadius: 12,
                          fontSize: "12.5px",
                          fontFamily: "monospace",
                          height: "100%",
                          maxHeight: "440px",
                          overflowY: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                        }}
                      >
                        {JSON.stringify(jsonReportData || statusResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    padding: "60px 20px",
                    textAlign: "center",
                    background: "#f8fafc",
                    border: "1px dashed #cbd5e1",
                    borderRadius: 14,
                  }}
                >
                  <RefreshCw style={{ width: 40, height: 40, color: "#94a3b8", margin: "0 auto 12px" }} />
                  <h5 style={{ margin: "0 0 6px", fontSize: "15px", color: "#334155" }}>
                    Generating GST Analytics Report...
                  </h5>
                  <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
                    Report generation is underway. Click "Refresh Status" to retrieve your download link.
                  </p>
                  <button
                    onClick={() => handleCheckStatus(requestId)}
                    style={styles.primaryBtn}
                  >
                    Refresh Status
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ────────────────────────────────────────────── */}
        <div
          style={{
            padding: "14px 28px",
            borderTop: "1px solid #e2e8f0",
            background: "#f8fafc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            {excelReport?.url && currentStep === 3 && (
              <a
                href={excelReport.url}
                target="_blank"
                rel="noopener noreferrer"
                download
                style={{
                  ...styles.primaryBtn,
                  background: "#16a34a",
                  color: "#ffffff",
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 18px",
                  fontSize: "13px",
                }}
              >
                <FileSpreadsheet style={{ width: 15, height: 15 }} />
                <span>Download Excel (.xlsx)</span>
              </a>
            )}
          </div>

          <button onClick={onClose} style={styles.outlineBtn}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Micro component for info cards ──────────────────────────────────────────
const ReportCard = ({ label, value, highlight = false }) => (
  <div
    style={{
      background: highlight ? "#f0fdf4" : "#ffffff",
      border: highlight ? "1.5px solid #86efac" : "1px solid #e2e8f0",
      borderRadius: 12,
      padding: "14px 16px",
    }}
  >
    <div style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 5 }}>
      {label}
    </div>
    <div style={{ fontSize: "14px", fontWeight: 700, color: highlight ? "#15803d" : "#0f172a", wordBreak: "break-word" }}>
      {value || "N/A"}
    </div>
  </div>
);

// ── Shared styling tokens ───────────────────────────────────────────────────
const styles = {
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 600,
    fontSize: "12.5px",
    color: "#334155",
  },
  input: {
    width: "100%",
    border: "1.5px solid #cbd5e1",
    borderRadius: 9,
    padding: "9px 14px",
    fontSize: "13.5px",
    color: "#0f172a",
    outline: "none",
    background: "#ffffff",
    boxSizing: "border-box",
  },
  primaryBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "#024e5a",
    color: "#ffffff",
    border: "none",
    borderRadius: 10,
    padding: "10px 20px",
    fontSize: "13.5px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "background 0.2s ease",
  },
  outlineBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#ffffff",
    color: "#334155",
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },
  th: {
    padding: "12px 14px",
    textAlign: "left",
    fontWeight: 700,
    color: "#475569",
    borderBottom: "1px solid #e2e8f0",
  },
  td: {
    padding: "12px 14px",
    color: "#1e293b",
  },
};
